import React, { useState, useEffect, useMemo, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import EditableBOMItems, { bomTotalCost } from '../../components/BOM/EditableBOMItems';
import { API_CONFIG, getApiUrl, apiFetch } from '../../config/api';
import { useMessages } from '../../components/Messages';
import { showApiError, parseApiError, getErrorMessage } from '../../utils/errorUtils';
import { useAuth } from '../../contexts/AuthContext';
import { hasRole } from '../../utils/roleUtils';
import './BOMs.css';
import { localToday } from '../../utils/dateUtils';

const ViewBOM = forwardRef(({ bomId, onBack, editMode = false, createMode = false, onSave, onCancel, onEdit, onBOMCreated, preselectedProductId }, ref) => {
  const { t } = useTranslation('production');
  const { t: tCommon } = useTranslation('common');
  const [bom, setBOM] = useState(null);
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [hasChanges, setHasChanges] = useState(false);
  const [products, setProducts] = useState([]);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [debouncedProductSearch, setDebouncedProductSearch] = useState('');
  const [productSearchText, setProductSearchText] = useState('');
  const [bomItems, setBomItems] = useState([]);
  const [productVersions, setProductVersions] = useState([]);

  const { showSuccess, showError, showWarning } = useMessages();
  const { user } = useAuth();
  // Same roles as POST /api/boms/{id}/recalculate-costs (backend/common/rbac.py)
  const canRecalculateCosts = hasRole(user, 'COSTING', 'MANAGER', 'ADMIN');
  const [recalculating, setRecalculating] = useState(false);

  const handleDuplicate = async () => {
    try {
      const response = await apiFetch(getApiUrl(`/api/boms/${bomId}/copy`), {
        method: 'POST'
      });

      if (response.ok) {
        const newBom = await response.json();
        showSuccess(t('boms.view.messages.bomDuplicated', 'BOM Duplicated'), t('boms.view.messages.bomDuplicatedSuccess', 'A copy of the BOM has been created.'));
        if (onBOMCreated) {
          onBOMCreated(newBom.bom_id);
        }
      } else {
        const parsed = await showApiError(response, showError, tCommon, t('boms.view.messages.failedToDuplicateBOM', 'Failed to duplicate BOM'));
        setError(getErrorMessage(parsed, tCommon));
      }
    } catch (error) {
      console.error('Error duplicating BOM:', error);
      const errorMessage = tCommon('errors.INTERNAL_SERVER_ERROR');
      setError(errorMessage);
      showError(t('boms.view.messages.failedToDuplicateBOM', 'Failed to duplicate BOM'), errorMessage);
    }
  };

  const handleRecalculateCosts = async () => {
    if (!window.confirm(t('boms.view.messages.recalculateCostsConfirmation'))) {
      return;
    }

    setRecalculating(true);
    try {
      const response = await apiFetch(getApiUrl(`/api/boms/${bomId}/recalculate-costs`), {
        method: 'POST'
      });

      if (response.ok) {
        const updated = await response.json();
        const oldCosts = Object.fromEntries(bomItems.map(item => [item.bom_item_id, parseFloat(item.unit_cost || 0)]));
        const changedLines = (updated.items || []).filter(
          item => Math.abs(parseFloat(item.unit_cost || 0) - (oldCosts[item.bom_item_id] ?? 0)) >= 0.00005
        ).length;
        showSuccess(
          t('boms.view.messages.costsRecalculated'),
          t('boms.view.messages.costsRecalculatedSummary', {
            count: changedLines,
            oldTotal: computedTotalCost.toFixed(4),
            newTotal: parseFloat(updated.total_cost || 0).toFixed(4)
          })
        );
        await fetchBOM();
        await fetchBOMItems();
      } else {
        await showApiError(response, showError, tCommon, t('boms.view.messages.failedToRecalculateCosts'));
      }
    } catch (error) {
      console.error('Error recalculating BOM costs:', error);
      showError(t('boms.view.messages.failedToRecalculateCosts'), tCommon('errors.INTERNAL_SERVER_ERROR'));
    } finally {
      setRecalculating(false);
    }
  };

  useImperativeHandle(ref, () => ({
    handleDelete,
    handleSave,
    handleDuplicate
  }));

  // Debounce product search — wait 300ms after typing stops
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedProductSearch(productSearchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [productSearchTerm]);

  // Fetch products when debounced search changes
  useEffect(() => {
    fetchProducts(debouncedProductSearch);
  }, [debouncedProductSearch]);

  useEffect(() => {
    if (bomId && !createMode) {
      fetchBOM();
      fetchBOMItems();
    } else if (createMode) {
      const newBOM = {
        product_id: preselectedProductId || '',
        bom_name: '',
        bom_version: 'v1.0',
        description: '',
        status: 'DRAFT',
        effective_date: localToday(),
        expiry_date: '',
        total_cost: 0,
        yield_percentage: 100,
        setup_time_minutes: 0,
        cycle_time_minutes: 0
      };
      setBOM(newBOM);
      setEditData(newBOM);
    }
  }, [bomId, createMode, preselectedProductId]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (editMode || createMode) {
          handleCancel();
        } else {
          if (onBack) onBack();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [editMode, createMode, onBack]);

  const fetchProducts = async (search = '') => {
    try {
      const params = new URLSearchParams({ page_size: '50' });
      if (search) params.set('search', search);
      const response = await apiFetch(getApiUrl(`/api/products?${params}`));
      if (response.ok) {
        const data = await response.json();
        setProducts(data.products || data || []);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchBOM = async () => {
    setLoading(true);
    try {
      const response = await apiFetch(getApiUrl(`/api/boms/${bomId}`));
      if (response.ok) {
        const data = await response.json();
        setBOM(data);
        setEditData(data);
        // Initialize product search text
        if (data.product_code && data.product_name) {
          setProductSearchText(`${data.product_code} - ${data.product_name}`);
        }
        // Fetch other BOM versions for this product
        if (data.product_id) {
          fetchProductVersions(data.product_id);
        }
      } else {
        setError(t('boms.view.error.fetchFailed'));
      }
    } catch (error) {
      console.error('Error fetching BOM:', error);
      setError(t('boms.view.error.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  const fetchBOMItems = async () => {
    if (!bomId) return;
    await fetchBOMItemsForBom(bomId);
  };

  const fetchBOMItemsForBom = async (id) => {
    if (!id) return;

    try {
      const response = await apiFetch(getApiUrl(`/api/boms/${id}/items`));
      if (response.ok) {
        const data = await response.json();
        // Map item_id to inventory_item_id for frontend compatibility
        const mappedItems = (data || []).map(item => ({
          ...item,
          inventory_item_id: item.item_id
        }));
        setBomItems(mappedItems);
      }
    } catch (error) {
      console.error('Error fetching BOM items:', error);
    }
  };

  const fetchProductVersions = async (productId) => {
    if (!productId) return;
    try {
      const response = await apiFetch(getApiUrl(`/api/boms?product_id=${productId}&page_size=100`));
      if (response.ok) {
        const data = await response.json();
        setProductVersions(data.boms || []);
      }
    } catch (error) {
      console.error('Error fetching product BOM versions:', error);
    }
  };

  const handleFieldChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setError(null);
      const url = createMode
        ? getApiUrl('/api/boms')
        : getApiUrl(`/api/boms/${bomId}`);

      const method = createMode ? 'POST' : 'PUT';

      // Clean up data - convert empty strings to null for optional date fields
      const cleanedData = {
        ...editData,
        total_cost: parseFloat(computedTotalCost.toFixed(4)),
        effective_date: editData.effective_date || null,
        expiry_date: editData.expiry_date || null
      };

      const response = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cleanedData)
      });

      if (response.ok) {
        const result = await response.json();
        setHasChanges(false);

        showSuccess(
          createMode ? t('boms.view.messages.bomCreated') : t('boms.view.messages.bomUpdated'),
          createMode ? t('boms.view.messages.bomCreatedSuccess') : t('boms.view.messages.bomUpdatedSuccess')
        );

        if (createMode && result.bom_id) {
          setBOM(result);
          setEditData(result);
          // Fetch items for the newly created BOM (will be empty initially)
          await fetchBOMItemsForBom(result.bom_id);
          if (onBOMCreated) {
            onBOMCreated(result.bom_id);
          }
        } else if (!createMode) {
          await fetchBOM();
        }

        if (onSave) onSave(result);
      } else {
        const errorData = await response.json();
        const parsed = parseApiError(errorData);
        const errorMessage = getErrorMessage(parsed, tCommon);
        setError(errorMessage);
        showError(
          createMode ? t('boms.view.messages.failedToCreateBOM') : t('boms.view.messages.failedToUpdateBOM'),
          errorMessage
        );
      }
    } catch (error) {
      console.error('Error saving BOM:', error);
      const errorMessage = tCommon('errors.INTERNAL_SERVER_ERROR');
      setError(errorMessage);
      showError(
        createMode ? t('boms.view.messages.failedToCreateBOM') : t('boms.view.messages.failedToUpdateBOM'),
        errorMessage
      );
    }
  };

  const handleCancel = () => {
    if (createMode) {
      if (onCancel) onCancel();
    } else {
      setEditData(bom);
      setHasChanges(false);
      if (onCancel) onCancel();
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('boms.view.messages.deleteConfirmation'))) {
      return;
    }

    try {
      const response = await apiFetch(getApiUrl(`/api/boms/${bomId}`), {
        method: 'DELETE'
      });

      if (response.ok) {
        showSuccess(t('boms.view.messages.bomDeleted'), t('boms.view.messages.bomDeletedSuccess'));
        if (onBack) onBack();
      } else {
        const parsed = await showApiError(response, showError, tCommon, t('boms.view.messages.failedToDeleteBOM'));
        setError(getErrorMessage(parsed, tCommon));
      }
    } catch (error) {
      console.error('Error deleting BOM:', error);
      const errorMessage = tCommon('errors.INTERNAL_SERVER_ERROR');
      setError(errorMessage);
      showError(t('boms.view.messages.failedToDeleteBOM'), errorMessage);
    }
  };

  const handleItemsChange = (updatedItems) => {
    setBomItems(updatedItems);
  };

  // Auto-compute total cost from BOM items: one line per group of alternatives (#322)
  const computedTotalCost = useMemo(() => bomTotalCost(bomItems), [bomItems]);

  const renderOverviewTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <h3>{t('boms.view.sections.bomInformation')}</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>{t('boms.view.fields.product')} *</label>
            {(editMode || createMode) ? (
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  list="bom-product-list"
                  value={productSearchText}
                  onChange={(e) => {
                    const value = e.target.value;
                    setProductSearchText(value);
                    setProductSearchTerm(value);
                    const match = products.find(p =>
                      `${p.product_code} - ${p.product_name}` === value
                    );
                    if (match) {
                      handleFieldChange('product_id', match.product_id || match.id);
                    } else if (!value) {
                      handleFieldChange('product_id', '');
                    }
                  }}
                  placeholder={t('boms.view.placeholders.selectProduct')}
                  required
                />
                <datalist id="bom-product-list">
                  {products.map(product => (
                    <option key={product.product_id || product.id} value={`${product.product_code} - ${product.product_name}`} />
                  ))}
                </datalist>
              </div>
            ) : (
              <input
                type="text"
                value={(() => {
                  const p = products.find(p => (p.product_id || p.id) == editData.product_id);
                  return p ? `${p.product_code} - ${p.product_name}` : '';
                })()}
                disabled
              />
            )}
          </div>
          <div className="form-group">
            <label>{t('boms.view.fields.bomName')} *</label>
            <input
              type="text"
              value={editData.bom_name || ''}
              onChange={(e) => handleFieldChange('bom_name', e.target.value)}
              disabled={!editMode && !createMode}
              required
            />
          </div>
          <div className="form-group">
            <label>{t('boms.view.fields.bomVersion')} *</label>
            <input
              type="text"
              value={editData.bom_version || ''}
              onChange={(e) => handleFieldChange('bom_version', e.target.value)}
              disabled={!editMode && !createMode}
              placeholder={t('boms.view.placeholders.bomVersion')}
              required
            />
          </div>
          <div className="form-group">
            <label>{t('boms.view.fields.status')} *</label>
            <select
              value={editData.status || 'DRAFT'}
              onChange={(e) => handleFieldChange('status', e.target.value)}
              disabled={!editMode && !createMode}
              required
            >
              <option value="DRAFT">{t('boms.view.status.draft')}</option>
              <option value="ACTIVE">{t('boms.view.status.active')}</option>
              <option value="OBSOLETE">{t('boms.view.status.obsolete')}</option>
            </select>
          </div>
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={editData.is_default || false}
                onChange={(e) => handleFieldChange('is_default', e.target.checked)}
                disabled={!editMode && !createMode}
              />
              <span>{t('boms.view.fields.defaultBOM')}</span>
            </label>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>{t('boms.view.fields.yieldPercentage')}</label>
            <input
              type="number"
              value={editData.yield_percentage || 100}
              onChange={(e) => handleFieldChange('yield_percentage', parseFloat(e.target.value) || 100)}
              disabled={!editMode && !createMode}
              min="0"
              max="100"
              step="0.01"
            />
          </div>
          <div className="form-group">
            <label>{t('boms.view.fields.totalCost')}</label>
            <input
              type="text"
              value={`$${computedTotalCost.toFixed(4)}`}
              disabled
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>{t('boms.view.fields.setupTime')}</label>
            <input
              type="number"
              value={editData.setup_time_minutes || 0}
              onChange={(e) => handleFieldChange('setup_time_minutes', parseInt(e.target.value) || 0)}
              disabled={!editMode && !createMode}
              min="0"
            />
          </div>
          <div className="form-group">
            <label>{t('boms.view.fields.cycleTime')}</label>
            <input
              type="number"
              value={editData.cycle_time_minutes || 0}
              onChange={(e) => handleFieldChange('cycle_time_minutes', parseInt(e.target.value) || 0)}
              disabled={!editMode && !createMode}
              min="0"
            />
          </div>
        </div>

        <div className="form-group full-width">
          <label>{t('boms.view.fields.description')}</label>
          <textarea
            value={editData.description || ''}
            onChange={(e) => handleFieldChange('description', e.target.value)}
            disabled={!editMode && !createMode}
            rows="3"
            placeholder={t('boms.view.placeholders.bomDescription')}
          />
        </div>
      </div>
    </div>
  );

  const renderItemsTab = () => {
    const currentBomId = bom?.bom_id || bomId;

    if (createMode && !currentBomId) {
      return (
        <div className="tab-content">
          <div className="info-message" style={{ padding: '2rem', textAlign: 'center' }}>
            <p>{t('boms.view.messages.saveFirstMessage')}</p>
          </div>
        </div>
      );
    }

    return (
      <div className="tab-content">
        <EditableBOMItems
          bomId={currentBomId}
          items={bomItems}
          onItemsChange={handleItemsChange}
          editMode={editMode || createMode}
        />
      </div>
    );
  };

  const renderNotesTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <h3>{t('boms.view.sections.bomDescriptionNotes')}</h3>
        <div className="form-grid">
          <div className="form-group full-width">
            <label>{t('boms.view.fields.description')}</label>
            <textarea
              value={editData.description || ''}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              disabled={!editMode && !createMode}
              placeholder={t('boms.view.placeholders.bomDescriptionNotes')}
              rows="6"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderVersionsTab = () => {
    const otherVersions = productVersions.filter(v => v.bom_id !== bomId);

    if (otherVersions.length === 0) {
      return (
        <div className="tab-content">
          <div className="info-message" style={{ padding: '2rem', textAlign: 'center' }}>
            <p>{t('boms.view.messages.noOtherVersions', 'No other versions exist for this product.')}</p>
          </div>
        </div>
      );
    }

    return (
      <div className="tab-content">
        <div className="form-section">
          <h3>{t('boms.view.sections.bomVersions', 'BOM Versions')}</h3>
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('boms.view.fields.bomName')}</th>
                <th>{t('boms.view.fields.bomVersion')}</th>
                <th>{t('boms.view.fields.status')}</th>
                <th>{t('boms.view.fields.totalCost')}</th>
                <th>{t('boms.view.fields.defaultBOM')}</th>
              </tr>
            </thead>
            <tbody>
              {otherVersions.map(v => (
                <tr
                  key={v.bom_id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => onBOMCreated && onBOMCreated(v.bom_id)}
                >
                  <td>{v.bom_name}</td>
                  <td>{v.bom_version}</td>
                  <td>
                    <span className={`status-badge ${
                      v.status === 'DRAFT' ? 'status-warning' :
                      v.status === 'ACTIVE' ? 'status-success' :
                      v.status === 'OBSOLETE' ? 'status-error' : 'status-inactive'
                    }`}>
                      {v.status}
                    </span>
                  </td>
                  <td>${parseFloat(v.total_cost || 0).toFixed(4)}</td>
                  <td>{v.is_default ? '✓' : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className="view-container"><div className="loading">{t('boms.view.loading')}</div></div>;
  }

  if (error) {
    return <div className="view-container"><div className="error-message">{error}</div></div>;
  }

  if (!bom && !createMode) {
    return <div className="view-container"><div className="loading">{t('boms.view.titles.noBOMFound')}</div></div>;
  }

  return (
    <div className="view-container">
      <div className="view-header">
        <div className="header-title">
          <button className="btn-back" onClick={onBack}>←</button>
          <h1>
{createMode ? t('boms.view.titles.newBOM') : editData.bom_name || t('boms.view.titles.bom')}
          </h1>
          {!createMode && editData.status && (
            <span className={`status-badge ${
              editData.status === 'DRAFT' ? 'status-warning' :
              editData.status === 'ACTIVE' ? 'status-success' :
              editData.status === 'OBSOLETE' ? 'status-error' : 'status-inactive'
            }`}>
              {editData.status}
            </span>
          )}
        </div>
        <div className="header-actions">
          {!createMode && !editMode && (
            <>
              <button className="btn-secondary" onClick={onEdit}>{t('boms.view.buttons.edit')}</button>
              <button className="btn-secondary" onClick={handleDuplicate}>{t('boms.view.buttons.duplicate', 'Duplicate')}</button>
              {canRecalculateCosts && (
                <button className="btn-secondary" onClick={handleRecalculateCosts} disabled={recalculating}>
                  {t('boms.view.buttons.recalculateCosts')}
                </button>
              )}
            </>
          )}
          {(createMode || editMode) && (
            <>
              <button className="btn-secondary" onClick={handleCancel}>{t('boms.view.buttons.cancel')}</button>
              <button className="btn-primary" onClick={handleSave}>
                {createMode ? t('boms.view.buttons.create') : t('boms.view.buttons.save')}
              </button>
            </>
          )}
        </div>
      </div>
      <div className="tabs">
        <div className="tab-list">
          <button
            className={`tab-item ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
{t('boms.view.tabs.overview')}
          </button>
          <button
            className={`tab-item ${activeTab === 'items' ? 'active' : ''}`}
            onClick={() => setActiveTab('items')}
          >
{t('boms.view.tabs.items')}
          </button>
          <button
            className={`tab-item ${activeTab === 'notes' ? 'active' : ''}`}
            onClick={() => setActiveTab('notes')}
          >
{t('boms.view.tabs.description')}
          </button>
          {!createMode && (
            <button
              className={`tab-item ${activeTab === 'versions' ? 'active' : ''}`}
              onClick={() => setActiveTab('versions')}
            >
{t('boms.view.tabs.versions', 'Versions')} {productVersions.filter(v => v.bom_id !== bomId).length > 0 ? `(${productVersions.filter(v => v.bom_id !== bomId).length})` : ''}
            </button>
          )}
        </div>

        <div className="tab-panels">
          {activeTab === 'overview' && renderOverviewTab()}
          {activeTab === 'items' && renderItemsTab()}
          {activeTab === 'notes' && renderNotesTab()}
          {activeTab === 'versions' && renderVersionsTab()}
        </div>
      </div>
    </div>
  );
});

ViewBOM.displayName = 'ViewBOM';

export default ViewBOM;